import { apiUrl } from "../apiUrl";

/**
 * Tenant-aware fetch wrapper.
 *
 * Every call automatically prefixes the URL with /api/{tenant}/...
 * and attaches the tenant-scoped auth token (if one exists) as a
 * Bearer header.
 *
 * Usage:
 *   const res = await apiFetch(tenant, "/all-projects");
 *   const data = await res.json();
 *
 * Or, for the common "just give me JSON or throw" case:
 *   const data = await apiJson(tenant, "/all-projects");
 */
export function apiFetch(tenant, path, options = {}) {
  if (!tenant) {
    // Fails loud on purpose — a silent fallback to a non-tenant URL
    // would just 404 against the new api.php, or worse, quietly hit
    // the wrong tenant if you ever add a "default" fallback later.
    throw new Error(
      `apiFetch called without a tenant slug (path: "${path}"). ` +
        `Make sure this component is rendered inside the /:tenant route ` +
        `and is reading it via useParams()/useTenant().`,
    );
  }

  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const url = `${apiUrl}/api/${tenant}${cleanPath}`;

  const token = localStorage.getItem(`token_${tenant}`);

  const isFormData = options.body instanceof FormData;

  const headers = {
    Accept: "application/json",
    ...(options.body && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  return fetch(url, { ...options, headers });
}

/**
 * Convenience helper: parses JSON and throws on non-2xx responses so
 * callers can use a plain try/catch instead of checking res.ok everywhere.
 */
export async function apiJson(tenant, path, options = {}) {
  const res = await apiFetch(tenant, path, options);

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Request failed: ${res.status}`);
  }

  if (res.status === 204) return null;
  return res.json();
}